import '../index.less';

import { Table, message } from 'antd';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/store';
import { useEffect, useMemo, useState } from 'react';

import { numberWithCommas } from '../../../helpers/number';
import { displayDateAndTime } from '../../../utils/datetime.util';

import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { getCollectionHistoryLogThunk } from '../../../services/collectionService/collectionThunk';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';

type CollectionHistoryProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const CollectionHistory: React.FC<CollectionHistoryProps> = (props) => {
  const { accountEnquiry } = props;
  const dispatch = useDispatch<AppDispatch>();

  const [historyLogData, setHistoryLogData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: historyLogData?.total ?? 0,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    currentPage: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      render: (_, __, index: number) =>
        (paginationState.current - 1) * paginationState.pageSize + index + 1,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Old Value',
      dataIndex: 'oldValueString',
      key: 'oldValueString',
    },
    {
      title: 'New Value',
      dataIndex: 'newValueString',
      key: 'newValueString',
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
  ];

  const fetchCollectionHistory = async (params?: any) => {
    setLoading(true);
    await dispatch(
      getCollectionHistoryLogThunk({
        ...params,
        accountDetailsId: accountEnquiry?.accountDetails?.id,
      })
    )
      .unwrap()
      .then((res) => {
        setHistoryLogData(res);
      })
      .catch((err) => {
        message.error('Failed to fetch collection history');
        console.error('Failed to fetch collection history', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination: TablePaginationConfig) => {
    fetchCollectionHistory({
      ...initialParams,
      ...filterState,
      pagination,
      accountDetailsId: accountEnquiry?.accountDetails?.id,
    });
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useEffect(() => {
    if (accountEnquiry?.accountDetails?.id) {
      fetchCollectionHistory({
        ...initialParams,
        accountDetailsId: accountEnquiry?.accountDetails?.id,
      });
    }
  }, [initialParams, accountEnquiry?.accountDetails?.id]);

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="info-container">
          <Table
            columns={columns}
            dataSource={historyLogData?.data}
            pagination={tablePagination}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CollectionHistory;
